// All map icons are rasterised into MapLibre sprite images at load time and
// rendered via symbol layers so the GPU can batch large feature counts without
// per-frame JS overhead.
//
// Rainfall icons encode 1-hour accumulated rainfall (mm/hr) via shape + colour:
//   Dry      (< 1 mm/hr)   — sun + cloud outline, neon blue
//   Light    (1–10 mm/hr)  — cloud + 2 rain lines, neon yellow
//   Moderate (10–25 mm/hr) — cloud + 4 rain lines, neon orange
//   Heavy    (≥ 25 mm/hr)  — cloud + 6 rain lines, neon red
//
// 140×140 viewBox. Single neon colour per intensity; three-layer SVG glow filter.

import type { Map } from "maplibre-gl";

import {
  ALERT_COLORS,
  RAIN_COLOR_DRY,
  RAIN_COLOR_LIGHT,
  RAIN_COLOR_MODERATE,
  RAIN_COLOR_HEAVY,
} from "./map-colors.ts";

const CLOUD_PATH_140 = "M30 80 A20 20 0 0 1 50 60 A25 25 0 0 1 90 60 A20 20 0 0 1 110 80 A20 20 0 0 1 90 100 L50 100 A20 20 0 0 1 30 80 Z";

// Mirrors CSS: drop-shadow(0 0 3px) drop-shadow(0 0 8px) drop-shadow(0 0 16px)
function glowFilter(color: string): string {
  const id = "g" + color.slice(1);
  return `<defs><filter id="${id}" x="-50%" y="-50%" width="200%" height="200%">` +
    `<feGaussianBlur in="SourceAlpha" stdDeviation="2" result="b1"/>` +
    `<feGaussianBlur in="SourceAlpha" stdDeviation="6" result="b2"/>` +
    `<feGaussianBlur in="SourceAlpha" stdDeviation="12" result="b3"/>` +
    `<feFlood flood-color="${color}" result="c"/>` +
    `<feComposite in="c" in2="b1" operator="in" result="g1"/>` +
    `<feComposite in="c" in2="b2" operator="in" result="g2"/>` +
    `<feComposite in="c" in2="b3" operator="in" result="g3"/>` +
    `<feMerge><feMergeNode in="g3"/><feMergeNode in="g2"/><feMergeNode in="g1"/>` +
    `<feMergeNode in="SourceGraphic"/></feMerge>` +
    `</filter></defs>`;
}

function rainfallIconSvg(color: string, lines: 0 | 2 | 4 | 6): string {
  const filterId = "g" + color.slice(1);
  const sun = lines === 0
    ? `<circle cx="90" cy="50" r="15" fill="none" stroke="${color}" stroke-width="5"/>`
    : "";
  const cloud = `<path d="${CLOUD_PATH_140}" fill="none" stroke="${color}" stroke-width="5"/>`;
  const xs = lines === 2 ? [52, 82]
           : lines === 4 ? [42, 62, 82, 102]
           : lines === 6 ? [38, 55, 70, 85, 100, 115]
           : [];
  const rainLines = (xs as number[]).map((x) =>
    `<line x1="${x}" y1="108" x2="${x - 3}" y2="126" stroke="${color}" stroke-width="4.5" stroke-linecap="round"/>`,
  ).join("");
  return `<svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 140 140">` +
    glowFilter(color) +
    `<g filter="url(#${filterId})">${sun}${cloud}${rainLines}</g>` +
    `</svg>`;
}

// Trend arrow SDF icons — white-on-transparent so MapLibre can recolour at runtime
export const TREND_UP_SDF     = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"><polygon points="7,1 1,13 13,13" fill="white"/></svg>`;
export const TREND_STABLE_SDF = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"><polygon points="1,1 1,13 13,7" fill="white"/></svg>`;
export const TREND_DOWN_SDF   = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"><polygon points="7,13 1,1 13,1" fill="white"/></svg>`;

export const RAINFALL_DRY_ICON      = rainfallIconSvg(RAIN_COLOR_DRY, 0);
export const RAINFALL_LIGHT_ICON    = rainfallIconSvg(RAIN_COLOR_LIGHT, 2);
export const RAINFALL_MODERATE_ICON = rainfallIconSvg(RAIN_COLOR_MODERATE, 4);
export const RAINFALL_HEAVY_ICON    = rainfallIconSvg(RAIN_COLOR_HEAVY, 6);

// River icon: 84×84 sprite, 280×280 viewBox, 70-unit margin for glow headroom.
// Used for both single-gauge sprites (icon-size 0.55 → 46px on screen) and
// multi-gauge HTML markers (CSS width 46px → 0.164 scale). Matching scales means
// identical glow rendering in both paths.
// stdDeviations are 2× glowFilter defaults to compensate for the smaller scale;
// stdDev 24 → ~4px glow on screen.
export function riverIconSvg(color: string): string {
  const id = "g" + color.slice(1);
  const d = "M140 70 L210 140 L140 210 L70 140 Z";
  const filter =
    `<defs><filter id="${id}" x="-50%" y="-50%" width="200%" height="200%">` +
    `<feGaussianBlur in="SourceAlpha" stdDeviation="4"  result="b1"/>` +
    `<feGaussianBlur in="SourceAlpha" stdDeviation="12" result="b2"/>` +
    `<feGaussianBlur in="SourceAlpha" stdDeviation="24" result="b3"/>` +
    `<feFlood flood-color="${color}" result="c"/>` +
    `<feComposite in="c" in2="b1" operator="in" result="g1"/>` +
    `<feComposite in="c" in2="b2" operator="in" result="g2"/>` +
    `<feComposite in="c" in2="b3" operator="in" result="g3"/>` +
    `<feMerge><feMergeNode in="g3"/><feMergeNode in="g2"/><feMergeNode in="g1"/>` +
    `<feMergeNode in="SourceGraphic"/></feMerge>` +
    `</filter></defs>`;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="84" height="84" viewBox="0 0 280 280">` +
    filter +
    `<g filter="url(#${id})">` +
    `<path d="${d}" fill="none" stroke="${color}" stroke-width="10" stroke-linejoin="round"/>` +
    `</g>` +
    `</svg>`;
}

const RIVER_ICON_NORMAL = riverIconSvg(ALERT_COLORS.normal!);
const RIVER_ICON_WATCH  = riverIconSvg(ALERT_COLORS.watch!);
const RIVER_ICON_HIGH   = riverIconSvg(ALERT_COLORS.high!);

// Hollow circle ring — SDF so MapLibre can recolour at runtime
const WELL_ICON_SDF = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24">
  <circle cx="12" cy="12" r="9" fill="white"/>
  <circle cx="12" cy="12" r="6.5" fill="black"/>
</svg>`;

function loadImage(map: Map, id: string, svg: string, size: number, sdf = false): Promise<void> {
  return new Promise((resolve, reject) => {
    const img = new Image(size, size);
    img.onload = () => {
      map.addImage(id, img, { sdf });
      resolve();
    };
    img.onerror = reject;
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });
}

export function loadIcons(map: Map): Promise<void> {
  return Promise.all([
    loadImage(map, "rainfall-dry",      RAINFALL_DRY_ICON,      56),
    loadImage(map, "rainfall-light",    RAINFALL_LIGHT_ICON,    56),
    loadImage(map, "rainfall-moderate", RAINFALL_MODERATE_ICON, 56),
    loadImage(map, "rainfall-heavy",    RAINFALL_HEAVY_ICON,    56),
    loadImage(map, "river-icon-normal", RIVER_ICON_NORMAL,      84),
    loadImage(map, "river-icon-watch",  RIVER_ICON_WATCH,       84),
    loadImage(map, "river-icon-high",   RIVER_ICON_HIGH,        84),
    loadImage(map, "well-icon",         WELL_ICON_SDF,          24, true),
    loadImage(map, "trend-up",          TREND_UP_SDF,           14, true),
    loadImage(map, "trend-stable",      TREND_STABLE_SDF,       14, true),
    loadImage(map, "trend-down",        TREND_DOWN_SDF,         14, true),
  ]).then();
}
